import React from 'react'
import { LiveConnectionState } from 'types'
import StatusBadges, { ConnectionStatus } from './StatusBadges'
import {
  GAP,
  PADDING,
  TEXT_SIZE,
  Z_INDEX,
  ANIMATION,
  INTERACTION,
  TRANSITION,
  STATUS_COLORS,
  BORDER_COLORS,
  SEMANTIC,
} from './chat/design-tokens'

interface AppHeaderProps {
  currentView: string
  goToLanding: () => void
  isDarkMode: boolean
  activeRoute: {
    label: string
    description: string
  }
  connectionState: LiveConnectionState
  backendStatus: {
    severity: 'error' | 'warn' | 'info' | 'success'
    mode: 'agents' | 'fallback' | 'voice' | 'idle'
    message: string
  }
  isChatVisible: boolean
  openChat: () => void
  isWebcamActive?: boolean
  isScreenShareActive?: boolean
  isLocationShared?: boolean
  locationData?: any
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  currentView,
  goToLanding,
  isDarkMode,
  activeRoute,
  connectionState,
  backendStatus,
  isChatVisible,
  openChat,
  isWebcamActive,
  isScreenShareActive,
  isLocationShared,
  locationData,
}) => {
  if (currentView === 'landing') return null

  // Flat severity colors — no pills, just sharp inline badges (from design tokens)
  const severityBg =
    backendStatus.severity === 'error'
      ? `${STATUS_COLORS.error} ${BORDER_COLORS.error}`
      : backendStatus.severity === 'warn'
        ? `${STATUS_COLORS.warning} ${BORDER_COLORS.warning}`
        : `${STATUS_COLORS.success} ${BORDER_COLORS.success}`

  const severityDot =
    backendStatus.severity === 'error'
      ? SEMANTIC.red
      : backendStatus.severity === 'warn'
        ? SEMANTIC.orange
        : SEMANTIC.green

  return (
    <header
      className={`fixed top-0 left-0 w-full px-4 py-3 flex flex-row justify-between items-center ${Z_INDEX.modal} pointer-events-auto border-b border-border/30 bg-background/95 backdrop-blur-sm`}
    >
      {/* LEFT — Logo + route badges */}
      <div className={`flex items-center ${GAP.md}`}>
        {/* Logo / home button — flat, no capsule */}
        <button
          type="button"
          onClick={goToLanding}
          className={`mr-1 px-2 py-1 ${TRANSITION.colors} ${INTERACTION.focusRing} hover:bg-secondary`}
          title="Back to Home"
        >
          <span
            className={`font-mono font-bold tracking-tighter text-base ${isDarkMode ? 'text-white' : 'text-black'}`}
          >
            F.B<span className="text-orange-500">/c</span>
          </span>
        </button>

        {currentView === 'chat' && (
          <div className={`flex items-center ${GAP.sm}`}>
            {/* Route status badge — flat, sharp rectangle */}
            <div className="relative group">
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 border border-border/60 bg-secondary/60 transition-colors duration-150 hover:bg-secondary`}
              >
                <ConnectionStatus
                  connected={
                    connectionState === LiveConnectionState.CONNECTED ||
                    backendStatus.mode === 'agents' ||
                    backendStatus.mode === 'fallback'
                  }
                  className="!w-1.5 !h-1.5"
                />
                <span
                  className={`${TEXT_SIZE.xs} font-mono font-semibold tracking-[0.18em] uppercase text-foreground`}
                >
                  {activeRoute.label}
                </span>
              </div>
              {/* Hover tooltip */}
              <div
                className={`absolute top-full mt-1.5 left-0 w-56 ${PADDING.md} border border-border bg-popover shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${Z_INDEX.modal} text-popover-foreground`}
              >
                <p className={`${TEXT_SIZE.xs} leading-relaxed font-medium text-muted-foreground`}>
                  {activeRoute.description}
                </p>
              </div>
            </div>

            {/* Backend mode badge — flat sharp box, hidden on mobile */}
            <div className="relative group items-center hidden sm:flex">
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 border transition-colors duration-150 ${severityBg}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${severityDot}`} />
                <span
                  className={`${TEXT_SIZE.xs} font-mono font-semibold tracking-[0.18em] uppercase`}
                >
                  {backendStatus.mode === 'agents'
                    ? 'Agents'
                    : backendStatus.mode === 'fallback'
                      ? 'Fallback'
                      : backendStatus.mode === 'voice'
                        ? 'Voice'
                        : 'Idle'}
                </span>
              </div>
              {/* Hover tooltip */}
              <div
                className={`absolute top-full mt-1.5 left-0 w-72 ${PADDING.md} border border-border bg-popover shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${Z_INDEX.modal} text-popover-foreground`}
              >
                <p className={`${TEXT_SIZE.xs} leading-relaxed font-medium text-muted-foreground`}>
                  {backendStatus.message}
                </p>
              </div>
            </div>

            <StatusBadges
              isWebcamActive={isWebcamActive}
              isScreenShareActive={isScreenShareActive}
              isLocationShared={isLocationShared}
              locationData={locationData}
              className="hidden lg:flex"
            />
          </div>
        )}
      </div>

      {/* RIGHT — Open Chat button (flat, high-contrast, no pill) */}
      {!isChatVisible && (
        <div className={ANIMATION.fadeInUp}>
          <button
            type="button"
            onClick={openChat}
            className={`flex items-center gap-2 px-4 py-2 border border-border bg-foreground text-background text-xs font-bold uppercase tracking-widest ${TRANSITION.colors} hover:bg-foreground/85 ${INTERACTION.focusRing}`}
          >
            <span>Open Chat</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="square"
              strokeLinejoin="miter"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </button>
        </div>
      )}
    </header>
  )
}

export default React.memo(AppHeader)
